import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject, map, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { skipErrorToast } from 'src/app/core/http/error-toast.token';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { NoSessionError, TokenRefreshService } from 'src/app/core/services/token-refresh.service';
import type { AccountSyncStatus } from '../models/account.models';

// ─── Event types ─────────────────────────────────────────────────────────────

/**
 * Mailbox-scoped SSE event (`GET /v1/inbox/events/mailbox/<id>`).
 *
 * A discriminated union rather than one interface of optionals, so `thread_id` can be
 * *required* on the members that carry a thread. That is what lets the consumer narrow on
 * `type` alone — the old shape forced a `event.thread_id &&` runtime guard that also
 * silently swallowed every event without a thread.
 */
export type RealtimeEvent =
  | { type: 'connected'; mailbox_id: number; timestamp?: string; id?: string }
  | {
      type: 'email_received';
      mailbox_id: number;
      thread_id: number;
      message_id?: number;
      subject?: string;
      sender_email?: string;
      timestamp: string;
      id?: string;
    }
  | {
      /** Thread changed (webhook), or newly created by an incremental sync pass. */
      type: 'thread_updated';
      mailbox_id: number;
      thread_id: number;
      timestamp: string;
      id?: string;
    }
  | {
      /**
       * Every `sync_status` transition on the mailbox: the enqueue-time claim
       * (`syncing`), completion (`idle`), failure (`error`), and the backend cron's
       * stuck-sync reclaim (`idle`). Carries no `thread_id` — it is about the mailbox,
       * not a thread, which is why the list-oriented gates in the consumer must not
       * stand in front of it.
       *
       * `last_sync_error` is user-facing text and is always present (`null` when there
       * is none), so it can be rendered verbatim and cleared on `null`.
       */
      type: 'sync_status_changed';
      mailbox_id: number;
      sync_status: AccountSyncStatus;
      last_sync_error: string | null;
      total_threads: number;
      timestamp: string;
      id?: string;
    };

export type UserEvent =
  | { type: 'connected'; user_id: number; unread_count?: number; resumed?: boolean; last_event_id?: string; id?: string }
  | {
      type: 'comment_mention';
      user_id: number;
      notification_id?: number;
      comment_id: number;
      thread_id: number;
      author_id: number;
      timestamp: string;
      thread_subject?: string;
      snippet?: string;
      id?: string;
    }
  | {
      type: 'thread_assignment';
      user_id: number;
      notification_id?: number;
      author_id: number;
      thread_ids: number[];
      timestamp: string;
      id?: string;
    }
  | {
      type: 'order_created';
      user_id: number;
      notification_id?: number;
      thread_id: number;
      thread_subject?: string;
      timestamp: string;
      id?: string;
    }
  | {
      // WB-7402 — background order creation failed. Without this the in-progress
      // artifact would keep spinning forever.
      type: 'order_creation_failed';
      user_id: number;
      notification_id?: number;
      thread_id: number;
      thread_subject?: string;
      /**
       * Generic by design — the backend refuses to put a raw AI/HTTP error in a
       * user toast (it would carry internal hostnames). Read `error_code`, not
       * this, to tell the user anything useful.
       */
      error?: string;
      /**
       * Machine code from the shared review-reason vocabulary
       * (`ReviewReasonCode`), the same one MTO publishes as `process_error`.
       */
      error_code?: string;
      timestamp: string;
      id?: string;
    }
  | {
      type: 'order_status_changed';
      user_id: number;
      notification_id?: number;
      order_id: number;
      order_name?: string | null;
      email_subject?: string | null;
      new_status: 'completed' | 'needs_review' | 'processing';
      timestamp: string;
      id?: string;
    }
  | {
      type: 'workflow_send_reply_failed';
      user_id: number;
      notification_id?: number;
      thread_id: number;
      draft_id: number;
      rule_id: number;
      thread_subject?: string;
      timestamp: string;
      id?: string;
    }
  | {
      // WB-7957/WB-8276 — async ERP sync status changed on an order. No
      // `notification_id`: this event is SSE-only, never a persisted notification.
      type: 'erp_sync_status_changed';
      user_id: number;
      order_id: number;
      order_name?: string | null;
      sync_status: string;
      /** Always present (null when unset) so the client can clear its error banner off going null. */
      error_type?: string | null;
      error?: string | null;
      erp_message?: string | null;
      timestamp: string;
      id?: string;
    };

// ─── Scope-based SSE (single endpoint: /v1/inbox/events/<scope>/<resource_id>) ─

type EventScope = 'mailbox' | 'user';

const RECONNECT_INITIAL_MS = 1000;
const RECONNECT_MAX_MS = 30000;

/**
 * Read the ticket out of the API envelope.
 *
 * `success_response()` routes a dict payload to `object` and a list to `data`,
 * so the ticket arrives under `object` — but both are accepted, along with a
 * bare body, so a revisited envelope does not silently break realtime.
 */
function extractTicket(response: unknown): string {
  for (const candidate of [
    (response as { object?: unknown })?.object,
    (response as { data?: unknown })?.data,
    response,
  ]) {
    const ticket = (candidate as { ticket?: unknown })?.ticket;
    if (typeof ticket === 'string' && ticket) {
      return ticket;
    }
  }
  throw new Error('No ticket in response');
}

interface ReconnectState {
  attempts: number;
  delayMs: number;
  timeoutId: ReturnType<typeof setTimeout> | null;
}

interface ConnectionEntry {
  subject: Subject<any>;
  eventSource: EventSource | null;
  disconnectedByUser: boolean;
  reconnect: ReconnectState;
}

@Injectable({
  providedIn: 'root',
})
export class RealtimeService {
  private connections = new Map<string, ConnectionEntry>();

  private readonly userEventsOnline = new BehaviorSubject<boolean>(false);

  /**
   * Whether the user-events stream is currently carrying live updates.
   *
   * Retrying is silent by design, so nothing on screen distinguishes "no orders
   * arrived" from "the connection is down" — which is how this failure went
   * unnoticed until a user reported it. Exposed for an offline indicator to
   * render; there is no consumer yet.
   */
  readonly liveUpdatesOnline$ = this.userEventsOnline.asObservable();

  constructor(
    private readonly tokenRefreshService: TokenRefreshService,
    private readonly http: HttpClient
  ) {}

  // ─── Auth & URL helpers ────────────────────────────────────────────────────

  private getCurrentUser(): { access_token?: string; id?: number; user_id?: number; user?: { id?: number } } | null {
    return LocalStorageService.get('currentUser') ?? null;
  }

  private getCurrentUserId(): number | null {
    const currentUser = this.getCurrentUser();
    return (
      currentUser?.id ??
      currentUser?.user_id ??
      currentUser?.user?.id ??
      null
    );
  }

  /** Build SSE URL: GET /v1/inbox/events/<scope>/<resource_id>?ticket=... */
  private buildEventsUrl(scope: EventScope, resourceId: number, ticket: string): string {
    const base = `${environment.BASE_URL_API}inbox/events/${scope}/${resourceId}`;
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}ticket=${encodeURIComponent(ticket)}`;
  }

  /**
   * Exchange the session for a single-use ticket to put in the SSE URL.
   *
   * `EventSource` cannot send an `Authorization` header, so whatever
   * authenticates the stream ends up in the URL — and a URL reaches the access
   * log, browser history and every proxy in between. This used to be the access
   * token itself, which decodes to the user's email and name. A ticket is
   * opaque, lives ~30 seconds and dies the first time it is used.
   */
  private requestTicket(): Observable<string> {
    return this.http
      .post<unknown>(`${environment.BASE_URL_API}inbox/events/ticket`, {}, {
        // A stream that cannot connect is a background condition, not something
        // to interrupt the user with. Without this the global interceptor raises
        // a toast per attempt, and the retry backoff turns that into a stack of
        // overlays over whatever the user was doing.
        context: skipErrorToast(),
      })
      .pipe(map((response) => extractTicket(response)));
  }

  // ─── Connection key ────────────────────────────────────────────────────────

  private connectionKey(scope: EventScope, resourceId: number): string {
    return `${scope}:${resourceId}`;
  }

  // ─── Reconnect helpers ─────────────────────────────────────────────────────

  private clearReconnect(key: string): void {
    const entry = this.connections.get(key);
    if (entry?.reconnect?.timeoutId != null) {
      clearTimeout(entry.reconnect.timeoutId);
      entry.reconnect.timeoutId = null;
    }
  }

  // ─── Single scoped connection (shared lifecycle) ───────────────────────────

  private openConnection<T extends RealtimeEvent | UserEvent>(
    key: string,
    scope: EventScope,
    resourceId: number,
    logLabel: string,
    parseEvent: (data: unknown) => T
  ): Observable<T> {
    const existing = this.connections.get(key);
    if (existing) {
      return existing.subject.asObservable() as Observable<T>;
    }

    const subject = new Subject<T>();
    const entry: ConnectionEntry = {
      subject,
      eventSource: null,
      disconnectedByUser: false,
      reconnect: { attempts: 0, delayMs: RECONNECT_INITIAL_MS, timeoutId: null },
    };
    this.connections.set(key, entry);

    /**
     * Back off and try again later. Deliberately never terminal.
     *
     * This used to give up after ten attempts — about two and a half minutes — and
     * delete the connection, leaving a full page reload as the only way back. Any
     * outage longer than that (a backend deploy, a laptop waking up) therefore froze
     * the page silently. The delay is capped, so retrying indefinitely costs about
     * two requests a minute.
     */
    const scheduleReconnect = (reason: string) => {
      if (entry.disconnectedByUser) return;

      this.setLiveUpdatesOnline(scope, false);

      const r = entry.reconnect;
      r.attempts += 1;

      const delay = Math.min(r.delayMs, RECONNECT_MAX_MS);
      r.delayMs = Math.min(r.delayMs * 2, RECONNECT_MAX_MS);
      r.timeoutId = setTimeout(() => {
        r.timeoutId = null;
        tryConnect();
      }, delay);

      console.warn(
        `[RealtimeService] ${reason}: ${logLabel}; reconnecting in ${delay}ms (attempt ${r.attempts})`
      );
    };

    const tryConnect = () => {
      if (entry.disconnectedByUser) return;

      // Renew the token before asking for a ticket, rather than replaying
      // whatever localStorage happens to hold. The access token lives ~15
      // minutes, and EventSource never passes through the HTTP interceptor that
      // renews tokens everywhere else — so the stream has no way back from an
      // expired one once it is connected.
      this.tokenRefreshService
        .getValidAccessToken()
        .pipe(switchMap(() => this.requestTicket()))
        .subscribe({
          next: (ticket) => {
            if (entry.disconnectedByUser) return;
            openStream(ticket);
          },
          error: (error: unknown) => {
            // The caller may have closed the stream while this was in flight.
            if (entry.disconnectedByUser) return;

            // No session at all is terminal — looping would achieve nothing. A
            // renewal or ticket request that merely failed is usually a blip,
            // so those retry.
            if (error instanceof NoSessionError) {
              this.setLiveUpdatesOnline(scope, false);
              subject.error(error);
              this.connections.delete(key);
              return;
            }
            scheduleReconnect('Could not obtain a connection ticket');
          },
        });
    };

    const openStream = (ticket: string) => {
      const url = this.buildEventsUrl(scope, resourceId, ticket);

      const eventSource = new EventSource(url);
      entry.eventSource = eventSource;

      eventSource.onopen = () => {
        entry.reconnect.attempts = 0;
        entry.reconnect.delayMs = RECONNECT_INITIAL_MS;
        entry.reconnect.timeoutId = null;
        this.setLiveUpdatesOnline(scope, true);
        console.log(`[RealtimeService] SSE opened: ${logLabel}`);
      };

      eventSource.onmessage = (event: MessageEvent) => {
        try {
          const data = parseEvent(JSON.parse(event.data));
          if (event.lastEventId) {
            (data as { id?: string }).id = event.lastEventId;
          }
          subject.next(data);
        } catch (err) {
          console.error('[RealtimeService] Parse error:', err);
        }
      };

      eventSource.onerror = () => {
        // Close and clear regardless of readyState
        eventSource.close();
        entry.eventSource = null;

        if (entry.disconnectedByUser) return;

        // Reconnect on any error state (CONNECTING or CLOSED), not just CLOSED —
        // the browser may fire onerror while still CONNECTING.
        scheduleReconnect('SSE error');
      };
    };

    tryConnect();
    return subject.asObservable();
  }

  /** Only the user-events stream backs the app-wide live-update indicator. */
  private setLiveUpdatesOnline(scope: EventScope, online: boolean): void {
    if (scope !== 'user') return;
    if (this.userEventsOnline.value !== online) {
      this.userEventsOnline.next(online);
    }
  }

  private closeConnection(key: string, logLabel: string): void {
    const entry = this.connections.get(key);
    if (!entry) return;

    entry.disconnectedByUser = true;
    this.setLiveUpdatesOnline(key.startsWith('user:') ? 'user' : 'mailbox', false);
    this.clearReconnect(key);

    const es = entry.eventSource;
    entry.eventSource = null;
    if (es) {
      es.onopen = null;
      es.onmessage = null;
      es.onerror = null;
      es.close();
    }

    entry.subject.complete();
    this.connections.delete(key);
    console.log(`[RealtimeService] Disconnected: ${logLabel}`);
  }

  // ─── Public API: mailbox events ────────────────────────────────────────────

  /**
   * Connect to real-time events for a mailbox.
   * GET /v1/inbox/events/mailbox/<mailbox_id>
   */
  connectMailboxEvents(mailboxId: number): Observable<RealtimeEvent> {
    // Fix #5: return EMPTY instead of leaking an uncompleted Subject
    if (mailboxId === -1) return EMPTY as Observable<RealtimeEvent>;

    const key = this.connectionKey('mailbox', mailboxId);
    return this.openConnection<RealtimeEvent>(
      key,
      'mailbox',
      mailboxId,
      `mailbox ${mailboxId}`,
      (d) => d as RealtimeEvent
    );
  }

  /**
   * Disconnect one mailbox stream, so callers can close only what left instead of
   * rebuilding every stream (each rebuild re-emits `connected`).
   */
  disconnectMailboxEvent(mailboxId: number): void {
    this.closeConnection(this.connectionKey('mailbox', mailboxId), `mailbox ${mailboxId}`);
  }

  /** Disconnect all mailbox streams. */
  disconnectMailboxEvents(): void {
    // Fix #1: delegate to closeConnection to avoid duplicated teardown logic
    const keys = Array.from(this.connections.keys()).filter((k) => k.startsWith('mailbox:'));
    keys.forEach((k) => {
      const id = k.split(':')[1];
      this.closeConnection(k, `mailbox ${id}`);
    });
    if (keys.length > 0) {
      console.log('[RealtimeService] Disconnected: all mailbox streams');
    }
  }

  // ─── Public API: user events ───────────────────────────────────────────────

  /**
   * Connect to the user-scoped events stream (e.g. comment_mention).
   * GET /v1/inbox/events/user/<user_id>
   */
  connectUserEvents(): Observable<UserEvent> {
    const userId = this.getCurrentUserId();
    if (userId == null) {
      console.warn('[RealtimeService] No user id for user-events SSE');
      return EMPTY as Observable<UserEvent>;
    }
    const key = this.connectionKey('user', userId);
    return this.openConnection<UserEvent>(
      key,
      'user',
      userId,
      'user-events',
      (d) => d as UserEvent
    );
  }

  /** Disconnect the user-events stream. */
  disconnectUserEvents(): void {
    const userId = this.getCurrentUserId();
    if (userId != null) {
      this.closeConnection(this.connectionKey('user', userId), 'user-events');
    } else {
      Array.from(this.connections.keys())
        .filter((k) => k.startsWith('user:'))
        .forEach((k) => this.closeConnection(k, 'user-events'));
    }
  }

  // ─── Public API: teardown ──────────────────────────────────────────────────

  /**
   * Disconnect all mailbox and user-events streams. Consumers tear down their own
   * stream type; this aggregate is what the spec resets the service with.
   */
  disconnectAll(): void {
    // Fix #6: use closeConnection per key for consistent teardown, then clear
    Array.from(this.connections.keys()).forEach((key) => {
      this.closeConnection(key, key);
    });
    console.log('[RealtimeService] Disconnected from all streams');
  }
}